import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { Listing, PurchaseResult, ShopView } from '../lib/types';
import { Button, EmptyState, ErrorState, Panel } from '../components/ui';
import { ListingCard } from '../components/ListingCard';
import { formatNumber, formatDate } from '../lib/format';
import { TrainerAvatar } from '../components/avatar/TrainerAvatar';

/** Somebody else's shop — the room you walk into when you follow a seller. */
export function BazaarShopPage() {
  const { username = '' } = useParams();
  const { me, setWallet, refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<ShopView>(() => api.get(`/bazaar/${username}`), [username]);
  const [busy, setBusy] = useState<string | null>(null);

  async function buy(listing: Listing) {
    setBusy(listing.id);
    try {
      const result = await api.post<PurchaseResult>(`/bazaar/listings/${listing.id}/buy`, {
        quantity: 1,
      });
      if (result.wallet) setWallet(result.wallet);
      push('reward', `Bought ${result.what} for ${formatNumber(result.total)} coins.`, '⛁');
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not buy that.');
      if (caught instanceof ApiError && caught.code === 'GONE') state.reload();
    } finally {
      setBusy(null);
    }
  }

  async function toggleFavorite() {
    try {
      const result = await api.post<{ favorite: boolean }>(`/bazaar/${username}/favorite`, {});
      push('success', result.favorite ? 'Added to your favourite sellers.' : 'Removed.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 380 }} />;

  const { bazaar, listings, isMine } = state.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div className="row" style={{ gap: 'var(--gap-sm)', alignItems: 'flex-start' }}>
          {/* The stallholder, beside their sign. A Bazaar is somebody's shop
              rather than a listings feed, and remembering who you bought from
              is most of what makes it one. */}
          <TrainerAvatar avatar={bazaar.ownerAvatar} size={80} loading="eager" />
          <div>
            <Link className="tiny muted" to="/bazaar">
              ← The Bazaar
            </Link>
            <h1 className="page-head__title">{bazaar.name}</h1>
            <div className="page-head__sub">
              {bazaar.greeting || (
                <>
                  Kept by <Link to={`/trainer/${bazaar.owner}`}>{bazaar.owner}</Link>.
                </>
              )}
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 'var(--gap-sm)' }}>
          {isMine ? (
            <Link className="btn btn--go" to="/bazaar/mine">
              Manage my stall
            </Link>
          ) : (
            <Button
              variant={bazaar.isFavorite ? 'gold' : 'default'}
              onClick={() => void toggleFavorite()}
            >
              {bazaar.isFavorite ? '★ Favourited' : '☆ Favourite'}
            </Button>
          )}
        </div>
      </div>

      <div className="pxframe pxframe--raised" style={{ overflow: 'hidden' }}>
        <div className={`awning awning--${bazaar.awningStyle}`} aria-hidden />
        <div className="shopfront__head">
          <div className="row" style={{ gap: 'var(--gap-lg)' }}>
            <span className="shop-stat">
              <span className="shop-stat__label">SOLD</span>
              {formatNumber(bazaar.totalSales)}
            </span>
            <span className="shop-stat">
              <span className="shop-stat__label">FAVOURITED</span>
              {formatNumber(bazaar.favoriteCount)}
            </span>
            <span className="shop-stat">
              <span className="shop-stat__label">TRADING SINCE</span>
              {formatDate(bazaar.createdAt)}
            </span>
          </div>
          <Link className="btn btn--sm" to={`/trainer/${bazaar.owner}`}>
            Trainer card
          </Link>
        </div>
      </div>

      <Panel title="◈ For sale" hint={`${listings.length} listing${listings.length === 1 ? '' : 's'}`}>
        {listings.length === 0 ? (
          <EmptyState art="⛁" title="The stall is bare">
            Nothing for sale here right now. Favourite the shop to hear when that changes.
          </EmptyState>
        ) : (
          <div className="stall-grid">
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                busy={busy === listing.id}
                showSeller={false}
                onBuy={listing.seller.id === me?.user.id ? undefined : (row) => void buy(row)}
              />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
